/**
 * This component will allow for urls containing a locale slug.
 * Eg: example.com/page/subpage
 * Eg: example.com/es/page/subpage
 * Eg: example.com/fr/page/subpage
 *
 * match.path will contain the locale slug (if present),
 * or a single '/' (default locale).
 *
 * Will update redux with the new locale when the locale slug within the path changes.
 *
 * Will render SiteLayout, passing through the next child component.
 */

// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import { Switch, Route } from 'react-router-dom'
import { localize } from 'i18n-calypso'

// Internal Dependencies:
import PageHeader from 'church-theme-library/pages/PageHeader'

import SiteLayout from 'church-theme-library/layouts/SiteLayout'
import HomeLayout from 'church-theme-library/layouts/HomeLayout'
import MainLayout from 'church-theme-library/layouts/MainLayout'
import NewsLayout from 'church-theme-library/layouts/NewsLayout'
import NewsCategoryLayout from 'church-theme-library/layouts/NewsCategoryLayout'
import EventsLayout from 'church-theme-library/layouts/EventsLayout'
import SermonsLayout from 'church-theme-library/layouts/SermonsLayout'
import SermonSeriesLayout from 'church-theme-library/layouts/SermonSeriesLayout'
import SpeakerLayout from 'church-theme-library/layouts/SpeakerLayout'
import BulletinsLayout from 'church-theme-library/layouts/BulletinsLayout'
import GalleriesLayout from 'church-theme-library/layouts/GalleriesLayout'
import ResourcesLayout from 'church-theme-library/layouts/ResourcesLayout'
import MinistriesLayout from 'church-theme-library/layouts/MinistriesLayout'
import VideosLayout from 'church-theme-library/layouts/VideosLayout'
import VideosPlaylistLayout from 'church-theme-library/layouts/VideosPlaylistLayout'
import VideosPlaylistSelectedItemLayout from 'church-theme-library/layouts/VideosPlaylistSelectedItemLayout'

import Page from 'church-theme-library/pages/Page'

// Redux:
import { setLocale } from 'church-theme-library/redux/actions/site'

class Locale extends React.Component {
	componentDidMount() {
		this.update()
	}

	componentDidUpdate() {
		this.update()
	}

	// Set the locale.
	update = () => {
		const { match, dispatch } = this.props
		let locale = match.path.replace( /\//g, '' )

		if ( ! locale ) {
			return
		}

		dispatch( setLocale( locale ) )
	}

	render() {
		const { match, translate, locale, slugs } = this.props

		return (
			<Switch>
				{/* Home */}
				<Route path={ match.path } exact render={ props => (
					<SiteLayout type="page" { ...props }>
						<HomeLayout type="page" { ...props } />
					</SiteLayout>
				)} />

				{/* Blog */}
				<Route path={ `${match.path}${slugs.blog}` } exact render={ props => (
					<SiteLayout type="posts" { ...props }>
						<NewsLayout type="posts" { ...props } />
					</SiteLayout>
				)} />

				{/* News */}
				<Route path={ `${match.path}${slugs.news}` } exact render={ props => (
					<SiteLayout type="posts" { ...props }>
						<NewsLayout type="posts" { ...props } />
					</SiteLayout>
				)} />

					{/* Blog - Category */}
					<Route path={ `${match.path}${slugs.blog}/${slugs.category}/:slug` } exact render={ props => (
						<SiteLayout type="posts-category" { ...props }>
							<NewsCategoryLayout type="posts-category" { ...props } />
						</SiteLayout>
					)} />

					{/* News - Category */}
					<Route path={ `${match.path}${slugs.news}/${slugs.category}/:slug` } exact render={ props => (
						<SiteLayout type="posts-category" { ...props }>
							<NewsCategoryLayout type="posts-category" { ...props } />
						</SiteLayout>
					)} />

					{/* Blog - Single */}
					<Route path={ `${match.path}${slugs.blog}/:slug` } exact render={ props => (
						<SiteLayout type="post" { ...props }>
							<MainLayout type="post" { ...props }>
								<Page type="post" { ...props } />
							</MainLayout>
						</SiteLayout>
					)} />

					{/* News - Single */}
					<Route path={ `${match.path}${slugs.news}/:slug` } exact render={ props => (
						<SiteLayout type="post" { ...props }>
							<MainLayout type="post" { ...props }>
								<Page type="post" { ...props } />
							</MainLayout>
						</SiteLayout>
					)} />

				{/* Events */}
				<Route path={ `${match.path}${slugs.events}` } exact render={ props => (
					<SiteLayout type="events" { ...props }>
						<PageHeader type="events" { ...props } />

						<EventsLayout type="events" { ...props } />
					</SiteLayout>
				)} />

					{/* Events - Category */}
					<Route path={ `${match.path}${slugs.events}/${slugs.category}/:slug` } exact render={ props => (
						<SiteLayout type="events-category" { ...props }>
							<PageHeader type="events-category" { ...props } />

							<EventsLayout type="events-category" { ...props } />
						</SiteLayout>
					)} />

					{/* Events - Single */}
					<Route path={ `${match.path}${slugs.events}/:slug` } exact render={ props => (
						<SiteLayout type="event" { ...props }>
							<MainLayout type="event" { ...props }>
								<Page type="event" { ...props } />
							</MainLayout>
						</SiteLayout>
					)} />

				{/* Sermons */}
				<Route path={ `${match.path}${slugs.sermons}` } exact render={ props => (
					<SiteLayout type="sermons" { ...props }>
						<SermonsLayout type="sermons" { ...props } />
					</SiteLayout>
				)} />

					{/* Sermons - Category */}
					<Route path={ `${match.path}${slugs.sermons}/${slugs.category}/:slug` } exact render={ props => (
						<SiteLayout type="sermons-category" { ...props }>
							<SermonsLayout type="sermons-category" { ...props } />
						</SiteLayout>
					)} />

					{/* Sermon Series */}
					<Route path={ `${match.path}${slugs.sermon_series}/:slug` } exact render={ props => (
						<SiteLayout type="sermon-series" { ...props }>
							<SermonSeriesLayout type="sermon-series" { ...props } />
						</SiteLayout>
					)} />

					{/* Sermons - Single */}
					<Route path={ `${match.path}${slugs.sermons}/:slug` } exact render={ props => (
						<SiteLayout type="sermon" { ...props }>
							<MainLayout type="sermon" { ...props }>
								<Page type="sermon" { ...props } />
							</MainLayout>
						</SiteLayout>
					)} />

					{/* Speaker - Single */}
					<Route path={ `${match.path}${slugs.speakers}/:slug` } exact render={ props => (
						<SiteLayout type="speaker" { ...props }>
							<SpeakerLayout type="speaker" { ...props } />
						</SiteLayout>
					)} />

				{/* Bulletins */}
				<Route path={ `${match.path}${slugs.bulletins}` } exact render={ props => (
					<SiteLayout type="bulletins" { ...props }>
						<PageHeader type="bulletins" { ...props } />

						<BulletinsLayout type="bulletins" { ...props } />
					</SiteLayout>
				)} />

					{/* Bulletins - Single */}
					<Route path={ `${match.path}${slugs.bulletins}/:slug` } exact render={ props => (
						<SiteLayout type="bulletin" { ...props }>
							<MainLayout type="bulletin" { ...props }>
								<Page type="bulletin" { ...props } />
							</MainLayout>
						</SiteLayout>
					)} />

				{/* Galleries */}
				<Route path={ `${match.path}${slugs.gallery}` } exact render={ props => (
					<SiteLayout type="galleries" { ...props }>
						<PageHeader type="galleries" { ...props } />

						<GalleriesLayout type="galleries" { ...props } />
					</SiteLayout>
				)} />

					{/* Galleries - Single */}
					<Route path={ `${match.path}${slugs.gallery}/:slug` } exact render={ props => (
						<SiteLayout type="gallery" { ...props }>
							<MainLayout type="gallery" { ...props }>
								<Page type="gallery" { ...props } />
							</MainLayout>
						</SiteLayout>
					)} />

				{/* Resources */}
				<Route path={ `${match.path}${slugs.resources}` } exact render={ props => (
					<SiteLayout type="resources" { ...props }>
						<PageHeader type="resources" { ...props } />

						<ResourcesLayout type="resources" { ...props } />
					</SiteLayout>
				)} />

					{/* Resources - Category */}
					<Route path={ `${match.path}${slugs.resources}/${slugs.category}/:slug` } exact render={ props => (
						<SiteLayout type="resources-category" { ...props }>
							<PageHeader type="resources-category" { ...props } />

							<ResourcesLayout type="resources-category" { ...props } />
						</SiteLayout>
					)} />

					{/* Resources - Single */}
					<Route path={ `${match.path}${slugs.resources}/:slug` } exact render={ props => (
						<SiteLayout type="resource" { ...props }>
							<MainLayout type="resource" { ...props }>
								<Page type="resource" { ...props } />
							</MainLayout>
						</SiteLayout>
					)} />

				{/* Ministries */}
				<Route path={ `${match.path}${slugs.ministries}` } exact render={ props => (
					<SiteLayout type="ministries" { ...props }>
						<PageHeader type="ministries" { ...props } />

						<MinistriesLayout type="ministries" { ...props } />
					</SiteLayout>
				)} />

					{/* Ministries - Single */}
					<Route path={ `${match.path}${slugs.ministries}/:slug` } exact render={ props => (
						<SiteLayout type="ministry" { ...props }>
							<MainLayout type="ministry" { ...props }>
								<Page type="ministry" { ...props } />
							</MainLayout>
						</SiteLayout>
					)} />

				{/* Videos - playlist */}
				<Route path={ `${match.path}${slugs.videos}/${slugs.category}/:slug` } exact render={ props => (
					<SiteLayout type="videos-playlist" { ...props }>
						<PageHeader type="videos-playlist" { ...props } />

						<VideosPlaylistLayout type="videos-playlist" { ...props } />
					</SiteLayout>
				)} />

					{/* Videos - playlist - single */}
					<Route path={ `${match.path}${slugs.videos}/${slugs.category}/:slug/:video_id` } exact render={ props => (
						<SiteLayout type="videos-playlist-item" { ...props }>
							<PageHeader type="videos-playlist-item" { ...props } />

							<VideosPlaylistSelectedItemLayout type="videos-playlist" { ...props } />
						</SiteLayout>
					)} />

				{/* Page */}
				<Route path={ `${match.path}:slug` } exact render={ props => {
					if ( props.match.params.slug == slugs.videos ) {
						return (
							<SiteLayout type="videos" { ...props }>
								<PageHeader type="videos" { ...props } />

								<VideosLayout type="videos" { ...props } />
							</SiteLayout>
						)
					} else {
						return (
							<SiteLayout type="page" { ...props }>
								<MainLayout type="page" { ...props }>
									<Page type="page" { ...props } />
								</MainLayout>
							</SiteLayout>
						)
					}
				} } />

				<Route path={ `${match.path}:slug/:archivePageNumber` } exact render={ props => (
					<SiteLayout type="page" { ...props }>
						<MainLayout type="page" { ...props }>
							<Page type="page" { ...props } />
						</MainLayout>
					</SiteLayout>
				)} />

				{/* Fallback */}
				<Route path={ match.path } render={ props => (
					<SiteLayout type="page" { ...props }>
						<MainLayout type="page" { ...props }>
							<Page type="page" fallback={ true } { ...props } />
						</MainLayout>
					</SiteLayout>
				)} />
			</Switch>
		)
	}
}

const mapStateToProps = ( state, ownProps ) => {
	return {
		locale: state.site.locale,
		slugs: state.site.slugs
	}
}

export default localize(
	connect( mapStateToProps )( Locale )
)
